import { debounce, isEqual } from "lodash-es";
import { useEffect, useMemo } from "react";
import { useLocation } from "react-router";
import { useNavigate } from "react-router-dom-v5-compat";

import { type IScrollPos, scrollToPosition } from "utilities/scroll/scroll";

import { type IBTPaginationHistoryState } from "commonComponents/btWrappers/BTPagination/BTPagination";

/**
 * Saves scroll position in history state on scroll end. Should be used in conjunction with useHistoryState to restore scroll position on mount.
 * Most list pages get this for free by passing `scrollRestorationConfig` to GridContainer; call this hook directly only for non-grid lists.
 * NOTE - This is intended for window level scrolling. If your table is doing the scroll management (non-window scroll bar), this likely won't work. In general,
 * list pages should be doing window level scrolling if possible.
 * @param jobIds Optional job IDs to save alongside scroll position. When provided, scroll restoration will be skipped if the job context has changed.
 * @param options.enabled When false, the scroll-end handler is not bound (no saving). Defaults to true. Lets a shared host call this hook unconditionally and toggle it on per consumer.
 */
export const useScrollPositionHistory = (
    jobIds?: number[],
    options?: { enabled?: boolean },
): void => {
    const enabled = options?.enabled ?? true;
    const navigate = useNavigate();
    const location = useLocation();

    const debouncedSaveScroll = useMemo(
        () =>
            debounce(() => {
                const scrollPosition: IScrollPos = {
                    posX: window.scrollX,
                    posY: window.scrollY,
                };

                const newState: IBTPaginationHistoryState = {
                    ...((location.state ?? {}) as IBTPaginationHistoryState),
                    scrollPosition,
                    jobIds: jobIds,
                };

                navigate(location.pathname + location.search, {
                    replace: true,
                    state: newState,
                });
            }, 50),
        [navigate, location.pathname, location.state, location.search, jobIds],
    );

    useEffect(() => {
        if (!enabled) {
            return;
        }
        window.onscrollend = debouncedSaveScroll;

        return () => {
            debouncedSaveScroll.cancel();
            window.onscrollend = null;
        };
    }, [debouncedSaveScroll, enabled]);
};

/**
 * Utility to restore the scroll position of the window level scrollbar.
 * Skips restoration when both storedJobIds and currentJobIds are provided but don't match.
 * @param scrollPosition Position to restore, if null, no action will be taken.
 * @param storedJobIds Job IDs saved alongside the scroll position in history state.
 * @param currentJobIds Currently active job IDs from the component's props.
 */
export const restoreScrollPosition = (
    scrollPosition?: IScrollPos,
    storedJobIds?: number[],
    currentJobIds?: number[],
) => {
    if (!scrollPosition) {
        return;
    }
    if (storedJobIds && currentJobIds && !isEqual(storedJobIds, currentJobIds)) {
        return;
    }
    scrollToPosition(scrollPosition);
};
